// contexts/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Simulation mode state (super_admin only)
  const [simulationMode, setSimulationMode] = useState(null); // { role, profile, permissions, label }

  // Liberado release gate (Spec Ops). false = restricted (gated features are
  // super_admin-only). Defaults restricted until the status is read.
  const [liberado, setLiberado] = useState(false);
  // Global dashboard banner (Spec Ops). { engaged, message } or null.
  const [banner, setBanner] = useState(null);
  // Billing/payment status for the current month. { status: 'paid'|'due'|'overdue', price, currency, dueDay, month, ... }
  const [payment, setPayment] = useState(null);
  // OpenAI credit estimate — the server only sends this to super_admin, so a null here
  // means "not yours to see" as often as it means "not configured". { remaining, low, … }
  const [openAiCredit, setOpenAiCredit] = useState(null);
  const refreshLiberado = async () => {
    try {
      const t = localStorage.getItem('token');
      if (!t) return;
      const res = await fetch(`${API_URL}/spec-ops/status`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${t}` },
      });
      const data = await res.json();
      if (data && data.success) {
        setLiberado(!!data.liberado?.engaged);
        setBanner(data.banner || null);
        setPayment(data.payment || null);
        setOpenAiCredit(data.openAiCredit || null);
      }
    } catch (_) { /* keep restricted on error */ }
  };
  useEffect(() => {
    if (!user) return;
    refreshLiberado();
    // Poll so the banner appears/disappears across every open dashboard within ~30s
    // of a super_admin toggling it — no reload needed.
    const id = setInterval(refreshLiberado, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Check if user is logged in on mount
  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyToken = async () => {
    try {
      const response = await fetch(`${API_URL}/auth/me`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setUser(data.user);
      } else {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (data.success) {
        localStorage.setItem('token', data.token);
        setToken(data.token);
        setUser(data.user);
        return { success: true, user: data.user };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'auth.serverError' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setSimulationMode(null); // Clear simulation on logout
  };

  // Start simulating a role/profile (super_admin only)
  const startSimulation = (roleData) => {
    if (!user || user.role !== 'super_admin') {
      console.warn('Only super_admin can use simulation mode');
      return false;
    }

    setSimulationMode({
      role: roleData.role,
      profile: roleData.profile || null,
      permissions: roleData.permissions || [],
      label: roleData.label || roleData.role
    });

    console.log('🎭 Simulation mode started:', roleData);
    return true;
  };

  // Stop simulation and return to real permissions
  const stopSimulation = () => {
    setSimulationMode(null);
    console.log('🎭 Simulation mode ended');
  };

  // Check if currently in simulation mode
  const isSimulating = () => !!simulationMode;

  // Check if user has access to a section
  const canAccess = (section) => {
    if (!user) return false;

    // SIMULATION MODE: If active, use simulated permissions instead
    if (simulationMode) {
      const simPerms = simulationMode.permissions || [];

      // Check if simulated role has wildcard permission
      if (simPerms.includes('*')) {
        return true;
      }

      // Check if simulated role has specific section permission
      return simPerms.includes(section);
    }

    // NEW: Use backend permissions array if available
    if (user.permissions && Array.isArray(user.permissions)) {
      // Check if user has wildcard permission (access to everything)
      if (user.permissions.includes('*')) {
        return true;
      }

      // Check if user has specific section permission
      return user.permissions.includes(section);
    }

    console.log(`⚠️ Using fallback permissions for section "${section}"`, {
      username: user.username,
      role: user.role,
      profile: user.profile
    });

    // FALLBACK: Old hardcoded logic for existing sessions (until they log out/in)
    const permissions = {
      super_admin: ['*'],
      admin: ['*'],
      super_user: {
        accounting: ['conversations', 'campaigns', 'adsets', 'ads', 'ad-performance', 'products', 'analytics', 'families', 'master-catalog', 'usos'],
        sales: ['conversations', 'campaigns', 'adsets', 'ads', 'ad-performance', 'products', 'analytics', 'families', 'master-catalog', 'usos']
      },
      user: {
        campaign_manager: ['campaign-overview', 'conversations', 'campaigns', 'adsets', 'ads', 'ad-performance', 'segmentacion', 'products', 'tracked-links', 'convo-flows', 'simulador', 'forecast', 'intelligence', 'ad-workflow'],
        salesman: ['conversations']
      }
    };

    // Super Admin and Admin have access to everything
    if (user.role === 'super_admin' || user.role === 'admin') {
      return true;
    }

    // Super User has profile-based access
    if (user.role === 'super_user' && user.profile) {
      return permissions.super_user[user.profile]?.includes(section) || false;
    }

    // User role depends on profile
    if (user.role === 'user' && user.profile) {
      return permissions.user[user.profile]?.includes(section) || false;
    }

    return false;
  };

  // Get the appropriate landing page for the current user
  const getLandingPage = () => {
    if (!user) return '/login';
    if (simulationMode) return '/';
    if (user.role === 'super_admin' || user.role === 'admin') return '/';
    return user.landingPage || '/';
  };

  // Check if user can manage users (only super_admin and admin)
  const canManageUsers = () => {
    if (!user) return false;

    // In simulation mode, check simulated role
    if (simulationMode) {
      return simulationMode.role === 'super_admin' || simulationMode.role === 'admin';
    }

    return user.role === 'super_admin' || user.role === 'admin';
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    canAccess,
    canManageUsers,
    getLandingPage,
    isAuthenticated: !!user,
    // Simulation mode (super_admin only)
    simulationMode,
    startSimulation,
    stopSimulation,
    isSimulating,
    // Liberado release gate
    liberado,
    refreshLiberado,
    // Global dashboard banner
    banner,
    refreshBanner: refreshLiberado,
    // Billing/payment status for the current month
    payment,
    // OpenAI credit (super_admin only; null for everyone else)
    openAiCredit,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
